import { DatePipe } from "@angular/common";
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, NgZone, ViewChild } from "@angular/core";
import { AbstractControl } from "@angular/forms";
import { RowSelectedEvent } from "ag-grid-community";
import { BehaviorSubject, Observable } from "rxjs";
import { map, switchMap, take } from "rxjs/operators";
import { Profile } from "../../../models/profile";
import { UserAssist } from "../../../models/userAssist";
import { CloudStorageService } from "../../../services/cloud-storage";
import { ConfirmService } from "../../../services/confirmation.service";
import { SiteParameterService } from "../../../services/siteparameter.service";

declare var ResizeObserver: any;

@Component({
  selector: 'app-assist-invitations',
  templateUrl: './assist-invitations.component.html',
  styles: [
  ]
})
export class AssistInvitationComponent implements OnInit, OnDestroy {

  @ViewChild('gridWrapper')
  set gridWrapper(ref: ElementRef<HTMLElement> | undefined) {
    if (!ref) {
      // Grid wrapper is being removed from the DOM —
      // clear the api reference so any in-flight observer callbacks bail out
      this.disconnectObserver();
      this.gridApi = undefined;
      this.gridColumnApi = undefined;
      return;
    }
    if (ref.nativeElement === this.observedElement) {
      return;
    }
    this.disconnectObserver();
    this.observedElement = ref.nativeElement;
    this.resizeObserver = new ResizeObserver((entries: { contentRect: { width: number; }; }[]) => {
      const width = entries[0]?.contentRect.width ?? 0;
      if (width > 0 && this.gridApi) {
        this.ngZone.run(() => this.resizeGrid());
      }
    });
    this.resizeObserver.observe(this.observedElement);
  }

  public showList: boolean = true;
  public showDetail: boolean = false;
  public showAddButton: boolean = true;
  public showUpdateForm: boolean = false;
  public showAddForm: boolean = false;
  public disableUpdate: boolean = false;
  public disableAdd: boolean = true;
  public showAssistUserIDFound: boolean = false;
  public showAssistUserIDNotFound: boolean = false;

  public selectedUserAssistId: string = "";
  public selectedAssistUserId: string = "";
  public selectedAssistUserEmail: string = "";
  public selectedAssistUserStartDate: string = "";
  public selectedAssistUserEndDate: string = "";

  public isNoEndDate: boolean = false;
  public noEndDateValue = new Date(2099, 11, 31); // December 31, 2099

  public addErrorMessage: string = "";
  public updateErrorMessage: string = "";
  public saving = false;

  public profile!: Profile;
  public invitations$?: Observable<UserAssist[]>;
  public selectedinvitation$?: Observable<UserAssist>;
  private gridApi: any;
  public noRowsTemplate: string = "";
  private gridColumnApi: any;
  private resizeObserver?: typeof ResizeObserver;
  private observedElement?: HTMLElement;

  private refresh$ = new BehaviorSubject<void>(undefined);

  private dateFormatter = (params: any): string => {
    if (!params.value) return '';
    const d = new Date(params.value);
    if (isNaN(d.getTime())) return '';
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  };

  private endDateFormatter = (params: any): string => {
    if (!params.value) return '';
    const d = new Date(params.value);
    if (isNaN(d.getTime())) return '';
    if (d.getFullYear() === this.noEndDateValue.getFullYear() &&
      d.getMonth() === this.noEndDateValue.getMonth() &&
      d.getDate() === this.noEndDateValue.getDate()) {
      return 'No End Date';
    }
    return this.dateFormatter(params);
  };

  columnDefs = [
    { field: 'assistUserEmail', headerName: 'Assistant Email', minWidth: 300 },
    { field: 'startDate', headerName: 'Start Date', valueFormatter: this.dateFormatter },
    { field: 'endDate', headerName: 'End Date', valueFormatter: this.endDateFormatter },
  ];

  defaultColumnDef = {
    sortable: true,
    resizable: true,
  };

  constructor(private siteParmService: SiteParameterService, private cloudStorageService: CloudStorageService,
    private datePipe: DatePipe, private confirmService: ConfirmService, private ngZone: NgZone) {
    this.noRowsTemplate = this.siteParmService.getGridNoResults();
  }

  invitationList(): void {
    this.profile = this.siteParmService.getStaticProfile();
    //this.invitations$ = this.cloudStorageService.getUserAssists(this.profile.userId);
    this.invitations$ = this.refresh$.pipe(
      switchMap(() => this.cloudStorageService.getUserAssists(this.profile.userId))
    );
  }

  onRowClicked(event: RowSelectedEvent) {
    this.selectedUserAssistId = event.data.userAssistID;
    this.selectedAssistUserId = event.data.assistUserID;
    this.selectedAssistUserEmail = event.data.assistUserEmail;
    this.selectedAssistUserStartDate = this.datePipe.transform(event.data.startDate, 'MM/dd/yyyy') ?? '';
    this.selectedAssistUserEndDate = this.datePipe.transform(event.data.endDate, 'MM/dd/yyyy') ?? '';

    this.showList = false;
    this.showDetail = true;
    this.showAddButton = false;
    this.showUpdateForm = true;
    this.showAddForm = false;
  }

  onAddClicked() {
    this.selectedAssistUserId = '';
    this.selectedAssistUserEmail = '';
    this.selectedAssistUserStartDate = this.datePipe.transform(new Date(), 'MM/dd/yyyy') ?? '';
    this.selectedAssistUserEndDate = '';
    this.showList = false;
    this.showDetail = true;
    this.showAddForm = true;
    this.showUpdateForm = false;
    this.showAddButton = false;
  }

  closeDetail() {
    this.selectedUserAssistId = "";
    this.selectedAssistUserId = "";
    this.selectedAssistUserEmail = "";
    this.selectedAssistUserStartDate = "";
    this.selectedAssistUserEndDate = "";

    this.showAssistUserIDFound = false;
    this.showAssistUserIDNotFound = false;

    this.disableAdd = true;
    //this.disableUpdate = true;

    this.showDetail = false;
    this.showList = true;
    this.showAddButton = true;

    if (this.gridApi && !this.gridApi.isDestroyed()) {
      this.gridApi.sizeColumnsToFit();
    }
  }

  inputFocused(event: any) {
    event.target.select()
  }

  addInvitation(formData: any): void {

    console.log(this.profile.userId, this.selectedAssistUserId,
      new Date(this.selectedAssistUserStartDate), new Date(this.selectedAssistUserEndDate));

    this.addErrorMessage = '';
    this.saving = true;
    this.cloudStorageService.addUserAssist(
      this.profile.userId, this.selectedAssistUserId,
      new Date(this.selectedAssistUserStartDate), new Date(this.selectedAssistUserEndDate))
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.saving = false;
          this.refresh$.next();
          this.closeDetail();
        },
        error: (err) => {
          this.saving = false;
          console.error('Failed to add invitation', err);
          this.addErrorMessage = 'Could not add invitation. Please try again.';
        }
      });

    this.closeDetail();
  }

  updateInvitation(formData: any): void {
    this.updateErrorMessage = '';
    this.saving = true;
    this.cloudStorageService.updateUserAssist(
      this.selectedUserAssistId, this.profile.userId, this.selectedAssistUserId,
      new Date(this.selectedAssistUserStartDate), new Date(this.selectedAssistUserEndDate))
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.saving = false;
          this.refresh$.next();
          this.closeDetail();
        },
        error: (err) => {
          this.saving = false;
          console.error('Failed to update invitation', err);
          this.updateErrorMessage = 'Could not save changes. Please try again.';
        }
      });
  }

  async deleteInvitation(): Promise<void> {
    const message = 'Are you sure you want to delete this Agent Invitation?';
    const confirmed = await this.confirmService.confirm('Delete Invitation', message);
    if (confirmed) {
      this.cloudStorageService.deleteUserAssist(this.selectedUserAssistId)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.refresh$.next();
            this.closeDetail();
          },
          error: (err) => {
            console.error('Failed to delete invitation', err);
            this.updateErrorMessage = 'Could not save changes. Please try again.';
          }
        });
    }
  }

  searchUserByEmailAddress(formAgentIdCtrl: AbstractControl) {
    var emailAddress = formAgentIdCtrl.value;

    this.cloudStorageService.getUserByEmailAddress(emailAddress)
      .pipe(take(1))
      .subscribe(user => {
        if (!user) {
          this.showAssistUserIDFound = false;
          this.showAssistUserIDNotFound = true;
        }
        else {
          this.showAssistUserIDFound = true;
          this.showAssistUserIDNotFound = false;     
          formAgentIdCtrl.setValue(user.email);
          this.selectedAssistUserEmail = user.email;
          this.selectedAssistUserId = user.userId;          
        }
        this.setAddButton();
      });
  }

  resetAddForm(): void {
    this.selectedUserAssistId = "";
    this.selectedAssistUserId = "";
    this.selectedAssistUserEmail = "";
    this.selectedAssistUserStartDate = this.datePipe.transform(new Date(), 'MM/dd/yyyy') ?? '';
    this.selectedAssistUserEndDate = "";
    this.isNoEndDate = false;
    //this.agentLinkAssociationID = 0;
    //this.agentID = "";
    //this.linkedAgentName = "";
    //this.channelID = 0;
    //this.regionID = 0;
    //this.associatedRegionID = 0;
    //this.associatedAgentID = "";
    //this.associatedAgentName = "";
    this.showAssistUserIDFound = false;
    this.showAssistUserIDNotFound = false;
    this.disableAdd = true;
  }

  setAddButton(): void {
    this.disableAdd = !this.showAssistUserIDFound;
  }

  checkNoEndDate(e: any): void {
    if (e.target.checked) {
      this.isNoEndDate = true;
      this.selectedAssistUserEndDate = this.datePipe.transform(this.noEndDateValue, 'MM/dd/yyyy') ?? '';
    } else {
      this.isNoEndDate = false;
      this.selectedAssistUserEndDate = '';
    }
  }

  ngOnInit(): void {
    this.invitationList();
  }

  //ngAfterViewInit() {
  //  this.resizeObserver = new ResizeObserver((entries: { contentRect: { width: number; }; }[]) => {
  //    const width = entries[0]?.contentRect.width ?? 0;
  //    if (width > 0 && this.gridApi) {
  //      this.ngZone.run(() => this.gridApi.sizeColumnsToFit());
  //    }
  //  });
  //  this.resizeObserver.observe(this.gridWrapper.nativeElement);
  //}

  ngOnDestroy() {
    this.disconnectObserver();
    this.gridApi = undefined;
    this.gridColumnApi = undefined;
  }

  private disconnectObserver() {
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
    this.observedElement = undefined;
  }

  onGridReady(params: any) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
    this.tryResize();
    //
  }

  onGridPreDestroyed() {
    this.gridApi = undefined;
    this.gridColumnApi = undefined;
  }

  private tryResize() {
    if (!this.observedElement || !this.gridApi) return;
    const width = this.observedElement.getBoundingClientRect().width;
    if (width > 0) {
      // setTimeout pushes this past the current change detection cycle,
      // giving the grid a tick to finish wiring itself up
      setTimeout(() => this.resizeGrid(), 0);
    }
  }

  private resizeGrid() {
    if (!this.gridApi) return;
    try {
      this.gridApi.sizeColumnsToFit();
    } catch (e) {
      // Grid was destroyed between the check and the call — discard the api
      this.gridApi = undefined;
      this.gridColumnApi = undefined;
    }
  }
}
